import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { noteCrashDetectorUser } from "utils/crashDetector";
import { addBreadcrumb, setContext, setUser } from "utils/sentry";

/**
 * Hook to automatically set Sentry user context when user data changes
 * This provides better error tracking by associating errors with specific users
 */
export function useSentryUserContext() {
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (user) {
      // Set user context for Sentry
      setUser({
        id: user.id,
        organizationId: user.organization?.id
      });

      // Persist identity in the crash detector's record so a crash report can
      // name the user who was logged in when the tab died
      noteCrashDetectorUser({
        id: user.id,
        organizationId: user.organization?.id
      });

      // Set additional context
      setContext("user_details", {
        role: user.role,
        organizationId: user.organization?.id
      });

      // Add breadcrumb for user login
      addBreadcrumb({
        message: "User logged in",
        category: "auth",
        level: "info",
        data: {
          userId: user.id,
          organizationId: user.organization?.id
        }
      });
    } else {
      // Clear user context on logout
      setUser(null);
      noteCrashDetectorUser(null);

      addBreadcrumb({
        message: "User logged out",
        category: "auth",
        level: "info"
      });
    }
  }, [user]);
}

export default useSentryUserContext;
